import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ClipboardText, UsersThree, CheckCircle, Archive, ArrowRight, Info, Megaphone, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
    status === "ACTIVE" ? "bg-success/10 text-[#00953e]" : "bg-[#F3F4F6] text-[#8a90a0]"}`}>
    {status === "ACTIVE" ? <CheckCircle size={12} weight="fill" /> : <Archive size={12} weight="fill" />}
    {status === "ACTIVE" ? "Active" : "Archived"}
  </span>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

export default function LeadForms() {
  const [forms, setForms] = useState(null);
  const [formsError, setFormsError] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([api.get("/forms"), api.get("/forms/mapping")]).then(([f, m]) => {
      if (f.status === "fulfilled") setForms(f.value.data.forms);
      else setFormsError(f.reason?.response?.data?.detail || "Could not load Instant Forms from Meta");
      if (m.status === "fulfilled") {
        setCampaigns(m.value.data.campaigns || []);
        setMapping(m.value.data.mapping || {});
      }
      setLoading(false);
    });
  }, []);

  const choose = async (campaignId, formId) => {
    const prev = { ...mapping };
    const next = { ...mapping };
    if (formId === "auto") delete next[campaignId];
    else next[campaignId] = formId;
    setMapping(next);
    try {
      await api.put("/forms/mapping", { campaign_id: campaignId, form_id: formId });
      toast.success("Form assignment saved");
    } catch {
      setMapping(prev);
      toast.error("Could not save the assignment — try again");
    }
  };

  const usedBy = (formId) => campaigns.filter((c) => mapping[c.id] === formId).length;

  return (
    <div className="space-y-8" data-testid="lead-forms-page">
        <div>
          <h2 className="font-heading text-lg font-extrabold tracking-tight text-[#0A0D14]">Instant Forms Library</h2>
          <p className="text-sm text-[#525866] mt-1">All lead forms on your connected Facebook Page. Pick which form each campaign's ads should use.</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
          </div>
        ) : formsError ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 flex items-start gap-3" data-testid="forms-error-state">
            <Warning size={20} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-sm text-amber-900">{formsError}</div>
              <p className="text-xs text-amber-800 mt-1">
                Connect Meta and set your Facebook Page first, then come back here.
              </p>
              <div className="mt-3 flex gap-2">
                <Link to="/connect" className="rounded-full bg-[#0A0D14] text-white text-xs font-bold px-4 py-2" data-testid="forms-error-connect-link">Connect Meta</Link>
                <Link to="/team" className="rounded-full border border-[#E5E7EB] bg-white text-xs font-bold px-4 py-2 text-[#0A0D14]" data-testid="forms-error-team-link">Publishing Settings</Link>
              </div>
            </div>
          </div>
        ) : forms && forms.length === 0 ? (
          <div className="rounded-xl border border-[#E5E7EB] bg-white p-8 text-center" data-testid="forms-empty-state">
            <ClipboardText size={28} weight="duotone" className="text-[#8a90a0] mx-auto" />
            <p className="mt-3 text-sm font-bold text-[#0A0D14]">No Instant Forms on your Page yet</p>
            <p className="mt-1 text-xs text-[#8a90a0]">Create one in Meta Ads Manager (Publishing Tools → Instant Forms) — it will appear here automatically.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(forms || []).map((f) => (
              <div key={f.id} data-testid={`form-card-${f.id}`}
                className="rounded-xl border border-[#E5E7EB] bg-white p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-10 w-10 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center shrink-0">
                      <ClipboardText size={20} weight="duotone" className="text-[#FF6B35]" />
                    </span>
                    <div className="min-w-0">
                      <div className="font-bold text-sm text-[#0A0D14] truncate" title={f.name}>{f.name}</div>
                      <div className="text-[11px] text-[#8a90a0] mt-0.5">Created {fmtDate(f.created_time)}</div>
                    </div>
                  </div>
                  <StatusBadge status={f.status} />
                </div>
                {f.questions?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {f.questions.slice(0, 6).map((q, i) => (
                      <span key={i} className="rounded-md bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-semibold text-[#525866]">{q}</span>
                    ))}
                    {f.questions.length > 6 && <span className="text-[11px] text-[#8a90a0]">+{f.questions.length - 6} more</span>}
                  </div>
                )}
                <div className="mt-4 pt-3 border-t border-[#F3F4F6] flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0A0D14]">
                    <UsersThree size={14} weight="duotone" className="text-[#0052FF]" /> {(f.leads_count || 0).toLocaleString()} leads collected
                  </span>
                  <span className="text-[11px] text-[#8a90a0]">
                    {usedBy(f.id) > 0 ? `Used by ${usedBy(f.id)} campaign${usedBy(f.id) > 1 ? "s" : ""}` : "Not assigned"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <h2 className="font-heading text-lg font-extrabold tracking-tight text-[#0A0D14]">Campaign → Form assignment</h2>
          <p className="text-sm text-[#525866] mt-1">When the AI team publishes an ad into a leads campaign, it attaches the form you choose here.</p>
          {loading ? (
            <Skeleton className="h-40 rounded-xl mt-4" />
          ) : campaigns.length === 0 ? (
            <div className="mt-4 rounded-xl border border-[#E5E7EB] bg-white p-6 text-center text-sm text-[#8a90a0]" data-testid="mapping-empty-state">
              No leads campaigns found in your synced ad accounts.
            </div>
          ) : (
            <div className="mt-4 rounded-xl border border-[#E5E7EB] bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E7EB] bg-[#FAFAFB] text-left text-[11px] uppercase tracking-wide text-[#8a90a0]">
                    <th className="px-5 py-3 font-bold">Leads campaign</th>
                    <th className="px-5 py-3 font-bold">Ad sets</th>
                    <th className="px-5 py-3 font-bold">Instant form for new ads</th>
                    <th className="px-5 py-3 font-bold">Resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.id} className="border-b border-[#F3F4F6] last:border-0" data-testid={`campaign-row-${c.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2.5">
                          <Megaphone size={16} weight="duotone" className="text-[#FF6B35]" />
                          <span className="font-bold text-[#0A0D14]">{c.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-[#525866]">{c.adset_count}</td>
                      <td className="px-5 py-4">
                        <Select value={mapping[c.id] || "auto"} onValueChange={(v) => choose(c.id, v)}>
                          <SelectTrigger className="w-64 h-9 text-sm font-semibold" data-testid={`form-select-${c.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Auto (inherit from running ads)</SelectItem>
                            {(forms || []).filter((f) => f.status === "ACTIVE").map((f) => (
                              <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4">
                        {!mapping[c.id] ? (
                          <span className="rounded-full bg-[#F3F4F6] px-2.5 py-1 text-[11px] font-bold text-[#525866]">Inherited from sibling ad</span>
                        ) : (
                          <span className="rounded-full bg-[#FF6B35]/10 px-2.5 py-1 text-[11px] font-bold text-[#E85A25]">Your selection</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-[#E5E7EB] bg-white p-5">
          <div className="flex items-center gap-2 text-sm font-bold text-[#0A0D14]"><Info size={16} weight="duotone" className="text-[#0052FF]" /> How the publish flow picks a form</div>
          <div className="mt-3 flex items-center gap-2 flex-wrap text-xs font-semibold text-[#525866]">
            <span className="rounded-lg border border-[#FF6B35]/30 bg-[#FF6B35]/5 px-3 py-2">1. Form you selected for the campaign</span>
            <ArrowRight size={14} className="text-[#8a90a0]" />
            <span className="rounded-lg border border-[#E5E7EB] px-3 py-2">2. Form used by the running ad in the same ad set</span>
            <ArrowRight size={14} className="text-[#8a90a0]" />
            <span className="rounded-lg border border-[#E5E7EB] px-3 py-2">3. Newest active form on your Page</span>
          </div>
        </div>
      </div>
  );
}
